import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Toaster, useSonner } from "sonner";

/** A stable portal preserves mounted toasts when moving into a modal's top layer. */
export function AppToaster() {
  const { t } = useTranslation();
  const { toasts } = useSonner();
  const hasProgress = toasts.some((toast) => toast.type === "loading");
  const [host, setHost] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    const container = document.createElement("div");
    container.dataset.appToaster = "";
    let modalOrder: HTMLDialogElement[] = [];
    const place = (records: MutationRecord[] = []) => {
      const open = Array.from(
        document.querySelectorAll<HTMLDialogElement>("dialog:modal")
      );
      modalOrder = modalOrder.filter((dialog) => open.includes(dialog));
      // Preserve opening order even for dialogs mounted in a different DOM order.
      for (const record of records) {
        if (
          record.type === "attributes" &&
          open.includes(record.target as HTMLDialogElement)
        ) {
          const dialog = record.target as HTMLDialogElement;
          modalOrder = modalOrder.filter((entry) => entry !== dialog);
          modalOrder.push(dialog);
        }
      }
      for (const dialog of open) {
        if (!modalOrder.includes(dialog)) modalOrder.push(dialog);
      }
      const parent = modalOrder.at(-1) ?? document.body;
      if (container.parentElement !== parent) parent.append(container);
    };
    place();
    setHost(container);
    const observer = new MutationObserver(place);
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["open"],
    });
    return () => {
      observer.disconnect();
      container.remove();
    };
  }, []);
  return host
    ? createPortal(
        <Toaster
          position="bottom-center"
          richColors
          closeButton
          expand={hasProgress}
          visibleToasts={hasProgress ? Math.max(3, toasts.length) : 3}
          containerAriaLabel={t("notifications.title")}
          toastOptions={{ closeButtonAriaLabel: t("common.close") }}
        />,
        host
      )
    : null;
}
